import { useCallback, useEffect, useState } from "react";
import { getRoleName, isAuthenticated } from "../../common/utils";
import { useNavigate, useLocation } from "react-router-dom";
import { authFirebase } from "../../config/firebase";
import { useDispatch, useSelector } from "react-redux";
import { Status, SignInProvider, FIREBASE_ERROR_MESSAGE, RESET_PASSWORD_MESSAGE, DEFAULT_ERROR_MESSAGE, CLIENT_TYPE } from "../../common/constants";
import { selector as signInApi, signIn } from "./loginSlice";
import { selector as sendResetPasswordEmailApi } from "../../components/modals/sendResetPasswordEmail/sendResetPasswordEmailSlice";
import { _updateUser } from "../users/userSlice";
import { selector as groupApi, groupList } from '../deviceManagement/groupSlice';
import {
  getAuth,
  signInWithPopup,
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  OAuthProvider,
} from "firebase/auth";
import useNotification from "../../components/notification/useNotification";
import { validateEmail } from "../../common/utils";

import { roleIds } from "../../config";

export const useLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch: any = useDispatch();
  const signInResponse = useSelector(signInApi);
  const forgotPasswordResponse = useSelector(sendResetPasswordEmailApi);
  const controller = new AbortController();
  const isListGroupsLoaded: boolean = useSelector(groupApi).isLoaded;
  const [userEmail, setUserEmail] = useState<string>("");
  const [isValidEmail, setIsValidEmail] = useState<boolean>(true);
  const [userPassword, setUserPassword] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showForgotPassword, setShowForgotPassword] = useState<boolean>(false);

  const { showSuccessNotification, showWarningNotification } =
    useNotification();

  const forgotPassword = (): void => {
    setShowForgotPassword((prev) => !prev);
  };

  useEffect(() => {
    if (signInResponse.status === Status.PENDING) {
      setIsLoading(true);
    } else if (signInResponse.status === Status.SUCCESS) {
      const cookies = document.cookie.split(";").reduce((acc, cookie) => {
        const [name, value] = cookie.trim().split("=");
        return { ...acc, [name]: value };
      }, {});
      dispatch(_updateUser(cookies));
      const userRoleName: string = getRoleName();
      if(isAuthenticated() && !isListGroupsLoaded && userRoleName !== roleIds.superAdmin) {
            dispatch(groupList());
        }
      goToDashboardIfLoggedIn();
    } else if (signInResponse.status === Status.FAILED) {
      setIsLoading(false);
      showWarningNotification(signInResponse.error);
    }
  }, [signInResponse.status]);

  useEffect(() => {
    if (forgotPasswordResponse.status === Status.PENDING) {
      setIsLoading(true);
    } else if(forgotPasswordResponse.status === Status.SUCCESS) {
      showSuccessNotification(RESET_PASSWORD_MESSAGE.SUCCESS);
      setIsLoading(false);
    } else if(forgotPasswordResponse.status === Status.FAILED) {
      showWarningNotification(forgotPasswordResponse.error);
      setIsLoading(false);
    }
  }, [forgotPasswordResponse.status])

  useEffect(() => {
    if (location.state?.successMessage) {
      showSuccessNotification(location.state.successMessage);
      navigate("/", { replace: true, state: {} });
    }
  }, [location.state, showSuccessNotification, navigate]);

  const handleOnChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setUserEmail(event.target.value);
      setIsValidEmail(validateEmail(event.target.value));
    },
    [setUserEmail, setIsValidEmail]
  );

  const handlePasswordChange: (
    event: React.ChangeEvent<HTMLInputElement>
  ) => void = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setUserPassword(event.target.value);
    },
    [setUserPassword]
  );

  const signinWithEmail: () => void = useCallback(
    async function () {
      try {
        const auth = getAuth();
        const userCredential = await signInWithEmailAndPassword(
          auth,
          userEmail,
          userPassword
        );
        const user = userCredential.user;
        const token = await user.getIdToken();

        dispatch(
          signIn({
            idToken: token,
            provider: SignInProvider.EMAIL,
            isPasswordEnable: true,
            clientType: CLIENT_TYPE.WEB
          })
        );
      } catch (error: any) {
        const message: string = getErrorMessage(error);
        showWarningNotification(message);
      }
    },
    [userEmail, userPassword, dispatch],
  );

  const signinWithMicrosoft: () => void = useCallback(
    async function () {
      try {
        const provider = new OAuthProvider(`${SignInProvider.MICROSOFT}.com`);
        const result = await signInWithPopup(authFirebase, provider);
        const user = result.user;
        const idToken = await user.getIdToken();
        dispatch(
          signIn({
            idToken,
            provider: SignInProvider.MICROSOFT,
            isPasswordEnable: false,
            clientType: CLIENT_TYPE.WEB,
          }),
        );
      } catch (error) {
        const message: string = getErrorMessage(error);
        if (message == FIREBASE_ERROR_MESSAGE["auth/cancelled-popup-request"]) {
          controller.abort();
        } else {
          showWarningNotification(message);
        }
      }
    },
    [dispatch],
  );

  const signinWithGoogle: () => void = useCallback(
    async function () {
      try {
        const auth = getAuth();
        const provider = new GoogleAuthProvider();
        const result = await signInWithPopup(auth, provider);
        const user = result.user;
        const idToken = await user.getIdToken();
        dispatch(
          signIn({
            idToken,
            provider: SignInProvider.GOOGLE,
            isPasswordEnable: false,
            clientType: CLIENT_TYPE.WEB,
          }),
        );
      } catch (error) {
        const message: string = getErrorMessage(error);
        if (message == FIREBASE_ERROR_MESSAGE["auth/cancelled-popup-request"]) {
          controller.abort();
        } else {
          showWarningNotification(message);
        }
      }
    },
    [dispatch],
  );

  const goToDashboardIfLoggedIn = useCallback((): void => {
    const canAccess: boolean = isAuthenticated();
    const userRoleName: string = getRoleName();
    if (canAccess) {
      const targetPath =
        userRoleName === roleIds.admin ||
        userRoleName === roleIds.operator ||
        userRoleName === roleIds.viewer
          ? "/dashboard"
          : userRoleName === roleIds.superAdmin
            ? "/analytics"
            : "/";
      navigate(targetPath);
    }
  }, [navigate]);

  const signInUser: (provider: SignInProvider) => void = useCallback(
    async function (provider) {
      switch (provider) {
        case SignInProvider.MICROSOFT:
          signinWithMicrosoft();
          break;
        case SignInProvider.GOOGLE:
          signinWithGoogle();
          break;
        case SignInProvider.EMAIL:
          signinWithEmail();
          break;
      }
    },
    [userEmail, userPassword],
  );

  const getErrorMessage = (err: unknown): string => {
    if (typeof err !== "object" || err === null) return DEFAULT_ERROR_MESSAGE;

    const { code, message } = err as { code?: string; message?: string };
    if (code && FIREBASE_ERROR_MESSAGE[code]) {
      return FIREBASE_ERROR_MESSAGE[code];
    }

    return message || DEFAULT_ERROR_MESSAGE;
  };

  useEffect(() => {
    goToDashboardIfLoggedIn();
  }, []);

  return {
    userEmail,
    isValidEmail,
    userPassword,
    isLoading,
    showForgotPassword,
    signInUser,
    setUserEmail,
    setIsLoading,
    handleOnChange,
    handlePasswordChange,
    setShowForgotPassword,
    forgotPassword,
  };
};
